import React, { ReactElement, useState } from "react";
import { useRouter } from "next/router";
import { useToast } from "@chakra-ui/react";
import styled from "styled-components";
import { FormProvider, useForm } from "react-hook-form";

import NavBrandLogo from "lib/svgs/NavBrandLogo";
import Text, { TextType } from "components/Text";
import FormInput from "components/beta/FormInput";
import Button, { ButtonTypes } from "components/beta/Button";
import InputValidationError from "components/InputValidationError";

import { RequestDemoParams } from "../types";
import { requestDemoRules } from "../validation";
import { validateEmail } from "lib/validateEmail";
import { formatEmail } from "lib/formatEmail";
import { requestDemo } from "../service";
import { ResponseStatus } from "lib/http/types";
import { PrimaryColorPalette } from "lib/styles/colors";
import responsiveStyling, { responsiveStylingMax } from "lib/responsive";

export default function OnboardRequestDemoReskinned(): ReactElement {
  const router = useRouter();
  const methods = useForm<RequestDemoParams>();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  const handleSubmitRequest = async (data: Partial<RequestDemoParams>) => {
    setIsLoading(true);
    success && setSuccess(false);

    const invalidEmail = validateEmail(data.email);
    if (!data.email || invalidEmail) {
      setError(invalidEmail);
      toast({
        description: invalidEmail,
        duration: 3000,
        isClosable: true,
        status: "error",
        title: "Please fix email and try again.",
      });
      setIsLoading(false);
      return;
    }

    error && setError(null);
    const formattedEmail = formatEmail(data.email);
    const requestDemoParams: RequestDemoParams = {
      ...data,
      email: formattedEmail,
      firstName: String(data.firstName),
    };

    const result = await requestDemo(requestDemoParams);
    if (result.status === ResponseStatus.SUCCESS) {
      setSuccess(true);
      toast({
        description: "Successfully submitted demo request.",
        duration: 3000,
        isClosable: true,
        status: "success",
        title: "Success",
      });
    } else {
      setError(`Error submitting demo request: ${result.error}`);
    }
    setIsLoading(false);
  };

  if (success) {
    return (
      <FormBody>
        <NavBrandLogo width={104} height={112} />
        <div style={{ width: "520px" }}>
          <Text
            type={TextType.HEADING_MEDIUM}
            fontColor={PrimaryColorPalette.MAIN_6}
            style={{ marginBottom: "1rem", textAlign: "center" }}
          >
            Thank you for your interest!
          </Text>
        </div>
        <Text
          type={TextType.PARAGRAPH}
          fontColor={PrimaryColorPalette.MAIN_6}
          style={{ marginBottom: "1.5rem", textAlign: "center" }}
        >
          We'll reach out soon to schedule your demo.
        </Text>
        <Button
          variant={ButtonTypes.PRIMARY}
          onClick={() => router.push("/login")}
          style={{ width: "100%" }}
          disabled={isLoading}
          isLoading={false}
        >
          Back to Sign In
        </Button>
      </FormBody>
    );
  }

  return (
    <FormBody>
      <NavBrandLogo width={104} height={112} />

      <Text
        type={TextType.HEADING_MEDIUM}
        fontColor={PrimaryColorPalette.MAIN_6}
      >
        Request a Demo
      </Text>
      <div style={{ width: "520px" }}>
        <Text
          type={TextType.HEADING_SMALL_LIGHT}
          fontColor={PrimaryColorPalette.MAIN_6}
          style={{
            marginBottom: "1rem",
            textAlign: "center",
          }}
        >
          See how 10ure can help your business
        </Text>
      </div>

      <FormProvider {...methods}>
        <StyledForm onSubmit={methods.handleSubmit(handleSubmitRequest)}>
          <FormInput
            name='email'
            placeholder='Email'
            rules={requestDemoRules.email}
            height='48px'
            width='100%'
          />
          <FormInput
            name='firstName'
            placeholder='First name'
            rules={requestDemoRules.firstName}
            height='48px'
            width='100%'
          />
          <FormInput
            name='lastName'
            placeholder='Last name'
            height='48px'
            width='100%'
          />
          <FormInput
            name='companyName'
            placeholder='Company name'
            height='48px'
            width='100%'
          />
          <FormInput
            name='phone'
            placeholder='Phone number'
            height='48px'
            width='100%'
          />

          {error && <InputValidationError message={error} />}

          <Button
            variant={ButtonTypes.PRIMARY}
            type='submit'
            disabled={isLoading}
            isLoading={isLoading}
            style={{ marginTop: "1rem", width: "100%" }}
          >
            Request Demo
          </Button>
        </StyledForm>
      </FormProvider>

      <SignInContainer>
        <span>
          <Text
            type={TextType.PARAGRAPH_SPAN}
            fontColor={PrimaryColorPalette.MAIN_6}
          >
            {`Already have an account? `}
          </Text>
          <Text
            type={TextType.PARAGRAPH_SPAN}
            fontColor={PrimaryColorPalette.MAIN_6}
            style={{ textDecoration: "underline", cursor: "pointer" }}
            onClick={() => router.push("/login")}
          >
            Sign in here
          </Text>
        </span>
      </SignInContainer>
    </FormBody>
  );
}

const FormBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  width: 100%;
  max-width: 540px;
  margin: 0 auto;
  margin-top: 10%;

  @media (max-width: 768px) {
    margin-top: 40px;
    max-width: 90%;
  }

  > svg {
    margin-bottom: 22px;
  }
`;

const StyledForm = styled.form`
  width: 100%;
  min-width: 280px;
  max-width: 280px;
  display: flex;
  flex-direction: column;

  ${responsiveStyling({
    ">768": `
      min-width: 560px;
      max-width: 560px;
    `,
  })}

  ${responsiveStylingMax({
    "<480": `
      min-width: 90%;
      max-width: 90%;
    `,
    "<768": `
      min-width: 90%;
      max-width: 90%;
    `,
  })}
`;

const SignInContainer = styled.div`
  margin-top: 1.5rem;
  text-align: center;
`;
